import * as THREE from 'three';

const COLOR_HOT = new THREE.Color(0xeaffd0);
const COLOR_CORE = new THREE.Color(0xb7ff6a);
const COLOR_HALO = new THREE.Color(0x6fd936);
const COLOR_HOT_END = new THREE.Color(0xfaffe6);
const COLOR_IMPACT = new THREE.Color(0xfaffe6);
const COLOR_STAR = new THREE.Color(0xc9ff7a);

/**
 * FPS-only mining laser. Unlike the shared `MiningBeam` used by NPCs and
 * the ship — which renders as axial billboards (great at distance, but
 * obviously a 2D plane when you stand right next to it) — this one uses
 * solid cylinders with a Fresnel-style soft-silhouette shader. The
 * cylinders are inherently volumetric: viewed from any angle the
 * silhouette fades to zero alpha while the centre stays bright, so the
 * beam reads as a true tube of plasma at FPS range.
 *
 * Three concentric cylinders (hot streak, saturated core, soft halo)
 * plus a camera-facing impact hot spot, slow-rotating star burst, and a
 * muzzle origin glow give the cutter the feel of a real industrial
 * mining tool rather than a flat decal.
 */
export class FpsMiningBeam {
  readonly object3D: THREE.Group;

  private readonly hot: THREE.Mesh;
  private readonly core: THREE.Mesh;
  private readonly halo: THREE.Mesh;
  private readonly hotMat: THREE.ShaderMaterial;
  private readonly coreMat: THREE.ShaderMaterial;
  private readonly haloMat: THREE.ShaderMaterial;
  private readonly hotOpacity: THREE.IUniform<number>;
  private readonly coreOpacity: THREE.IUniform<number>;
  private readonly haloOpacity: THREE.IUniform<number>;
  private readonly hotTime: THREE.IUniform<number>;
  private readonly coreTime: THREE.IUniform<number>;
  private readonly haloTime: THREE.IUniform<number>;

  private readonly impactSprite: THREE.Sprite;
  private readonly impactStar: THREE.Sprite;
  private readonly originSprite: THREE.Sprite;
  private readonly impactSpriteMat: THREE.SpriteMaterial;
  private readonly impactStarMat: THREE.SpriteMaterial;
  private readonly originSpriteMat: THREE.SpriteMaterial;

  private readonly dotTex: THREE.DataTexture;
  private readonly starTex: THREE.DataTexture;

  private readonly tmpDir = new THREE.Vector3();
  private readonly tmpMid = new THREE.Vector3();
  private readonly tmpUp = new THREE.Vector3(0, 1, 0);
  private readonly tmpQuat = new THREE.Quaternion();

  private active = false;
  private pulse = 0;
  private impactPulse = 0;
  private elapsed = 0;

  constructor() {
    this.object3D = new THREE.Group();
    this.object3D.name = 'fps-mining-beam';
    this.object3D.visible = false;

    this.dotTex = createSoftDotTexture();
    this.starTex = createStarBurstTexture();

    // ── Hot streak — razor-thin near-white core. The actual "laser" line.
    // Additive blending lets opacity > 1 push the core into saturated
    // bloom territory, which sells the "this is a focused beam" read.
    this.hotMat = createBeamShaderMaterial(COLOR_HOT, COLOR_HOT_END, 6.0, 1.7);
    this.hotOpacity = this.hotMat.uniforms.uOpacity as THREE.IUniform<number>;
    this.hotTime = this.hotMat.uniforms.uTime as THREE.IUniform<number>;
    this.hot = new THREE.Mesh(buildBeamCylinder(0.011), this.hotMat);
    this.hot.renderOrder = 4;

    // ── Core — thin saturated-green plasma sleeve around the streak.
    this.coreMat = createBeamShaderMaterial(COLOR_CORE, COLOR_HOT, 2.6, 1.15);
    this.coreOpacity = this.coreMat.uniforms.uOpacity as THREE.IUniform<number>;
    this.coreTime = this.coreMat.uniforms.uTime as THREE.IUniform<number>;
    this.core = new THREE.Mesh(buildBeamCylinder(0.040), this.coreMat);
    this.core.renderOrder = 3;

    // ── Halo — soft luminous bloom; small enough to read as light around
    // the beam, not as a fat tube.
    this.haloMat = createBeamShaderMaterial(COLOR_HALO, COLOR_CORE, 0.9, 0.55);
    this.haloOpacity = this.haloMat.uniforms.uOpacity as THREE.IUniform<number>;
    this.haloTime = this.haloMat.uniforms.uTime as THREE.IUniform<number>;
    this.halo = new THREE.Mesh(buildBeamCylinder(0.14), this.haloMat);
    this.halo.renderOrder = 2;

    this.object3D.add(this.halo, this.core, this.hot);

    // ── Impact hot spot.
    this.impactSpriteMat = new THREE.SpriteMaterial({
      map: this.dotTex,
      color: COLOR_IMPACT,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    });
    this.impactSprite = new THREE.Sprite(this.impactSpriteMat);
    this.impactSprite.renderOrder = 5;
    this.impactSprite.scale.set(0.32, 0.32, 1);
    this.object3D.add(this.impactSprite);

    // ── 4-point star burst over the impact, slow-rotating for life.
    this.impactStarMat = new THREE.SpriteMaterial({
      map: this.starTex,
      color: COLOR_STAR,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    });
    this.impactStar = new THREE.Sprite(this.impactStarMat);
    this.impactStar.renderOrder = 5;
    this.impactStar.scale.set(1.4, 1.4, 1);
    this.object3D.add(this.impactStar);

    // ── Muzzle glow at the origin.
    this.originSpriteMat = new THREE.SpriteMaterial({
      map: this.dotTex,
      color: COLOR_HOT,
      transparent: true,
      opacity: 0.85,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    });
    this.originSprite = new THREE.Sprite(this.originSpriteMat);
    this.originSprite.renderOrder = 5;
    this.originSprite.scale.set(0.18, 0.18, 1);
    this.object3D.add(this.originSprite);
  }

  setEndpoints(from: THREE.Vector3, to: THREE.Vector3): void {
    this.active = true;
    this.object3D.visible = true;

    this.tmpDir.copy(to).sub(from);
    const length = this.tmpDir.length();
    if (length < 1e-4) {
      this.object3D.visible = false;
      return;
    }
    this.tmpDir.multiplyScalar(1 / length);

    this.tmpMid.copy(from).addScaledVector(this.tmpDir, length * 0.5);
    this.tmpQuat.setFromUnitVectors(this.tmpUp, this.tmpDir);

    for (const mesh of [this.hot, this.core, this.halo]) {
      mesh.position.copy(this.tmpMid);
      mesh.quaternion.copy(this.tmpQuat);
      mesh.scale.set(1, length, 1);
    }

    this.impactSprite.position.copy(to);
    this.impactStar.position.copy(to);
    this.originSprite.position.copy(from);
  }

  tick(dt: number): void {
    if (!this.active) return;
    this.elapsed += dt;
    this.pulse += dt * 9;
    this.impactPulse += dt * 16;

    const fast = 0.85 + Math.sin(this.pulse) * 0.15;
    const slow = 0.85 + Math.sin(this.pulse * 0.45) * 0.15;
    const jitter = 0.92 + Math.sin(this.pulse * 3.7) * 0.08;

    this.hotOpacity.value = (1.65 + (1 - fast) * 0.10) * jitter;
    this.coreOpacity.value = 1.00 * fast + 0.25;
    this.haloOpacity.value = 0.42 * slow + 0.18;
    this.hotTime.value = this.elapsed;
    this.coreTime.value = this.elapsed;
    this.haloTime.value = this.elapsed;

    const impactScale = 0.32 * (0.85 + Math.sin(this.impactPulse) * 0.12 + Math.sin(this.impactPulse * 2.7) * 0.06);
    this.impactSprite.scale.set(impactScale, impactScale, 1);
    this.impactSpriteMat.opacity = 0.9 + Math.sin(this.impactPulse * 1.3) * 0.1;

    const starScale = 1.4 * (0.85 + Math.sin(this.impactPulse * 1.6) * 0.15);
    this.impactStar.scale.set(starScale, starScale, 1);
    this.impactStarMat.rotation = this.impactPulse * 0.35;
    this.impactStarMat.opacity = 0.55 + Math.sin(this.impactPulse * 0.8) * 0.25;

    this.originSpriteMat.opacity = 0.7 + Math.sin(this.pulse * 1.4) * 0.2;
  }

  hide(): void {
    this.active = false;
    this.object3D.visible = false;
  }

  dispose(): void {
    this.hot.geometry.dispose();
    this.core.geometry.dispose();
    this.halo.geometry.dispose();
    this.hotMat.dispose();
    this.coreMat.dispose();
    this.haloMat.dispose();
    this.impactSpriteMat.dispose();
    this.impactStarMat.dispose();
    this.originSpriteMat.dispose();
    this.dotTex.dispose();
    this.starTex.dispose();
  }
}

/** Open-ended cylinder along Y, unit length, given radius. The shader
 *  treats it as a soft tube — caps are skipped so we never see flat
 *  endcap polygons popping into view at oblique angles. */
function buildBeamCylinder(radius: number): THREE.CylinderGeometry {
  return new THREE.CylinderGeometry(radius, radius, 1, 18, 1, true);
}

/** A `MeshBasicMaterial`-like additive shader that fades alpha along the
 *  silhouette via a Fresnel-style facing factor. The result is a tube
 *  that reads as solid plasma from any angle — no facets, no billboard
 *  reveal, no popping when the camera moves around it.
 *
 *  - `sharpness` controls how concentrated the bright centre is. Higher
 *    values give a thin intense streak; lower values give a broad soft
 *    glow that's still volumetrically lit.
 *  - `tipColor` is mixed in near the impact end of the beam so the
 *    contact point reads visibly hotter than the origin. */
function createBeamShaderMaterial(
  baseColor: THREE.Color,
  tipColor: THREE.Color,
  sharpness: number,
  opacity: number
): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: baseColor.clone() },
      uTipColor: { value: tipColor.clone() },
      uSharpness: { value: sharpness },
      uOpacity: { value: opacity },
      uTime: { value: 0 }
    },
    vertexShader: BEAM_VERTEX,
    fragmentShader: BEAM_FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    toneMapped: false
  });
}

const BEAM_VERTEX = /* glsl */ `
  varying vec3 vViewNormal;
  varying vec3 vViewDir;
  varying float vAlongAxis;

  void main() {
    vec4 mvPos = modelViewMatrix * vec4(position, 1.0);
    vViewNormal = normalize(normalMatrix * normal);
    vViewDir = normalize(-mvPos.xyz);
    // CylinderGeometry default extends from y = -0.5 to y = +0.5.
    // Map that into [0, 1] along the beam axis (origin → impact).
    vAlongAxis = position.y + 0.5;
    gl_Position = projectionMatrix * mvPos;
  }
`;

const BEAM_FRAGMENT = /* glsl */ `
  varying vec3 vViewNormal;
  varying vec3 vViewDir;
  varying float vAlongAxis;

  uniform vec3 uColor;
  uniform vec3 uTipColor;
  uniform float uSharpness;
  uniform float uOpacity;
  uniform float uTime;

  void main() {
    // Facing factor: 1 where the surface normal points at the camera
    // (centre of the visible tube), 0 at the silhouette. Powering by
    // uSharpness shapes the radial falloff.
    float facing = abs(dot(normalize(vViewNormal), normalize(vViewDir)));
    float radial = pow(facing, uSharpness);

    // Subtle scrolling wave so the plasma doesn't feel static. Low
    // amplitude so it reads as "alive" without strobing.
    float wave = 0.92 + 0.08 * sin((vAlongAxis * 6.0 - uTime * 5.5) * 3.14159);

    // Contact-end colour shift — last 30% of the beam length tints toward
    // the hotter tip colour, selling heat buildup at the impact.
    float tipMix = smoothstep(0.65, 1.0, vAlongAxis);
    vec3 col = mix(uColor, uTipColor, tipMix * 0.55);

    float alpha = radial * uOpacity * wave;
    if (alpha < 0.002) discard;
    gl_FragColor = vec4(col, alpha);
  }
`;

function createSoftDotTexture(): THREE.DataTexture {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - c) / c;
      const dy = (y - c) / c;
      const r = Math.min(1, Math.sqrt(dx * dx + dy * dy));
      const alpha = Math.pow(Math.max(0, 1 - r), 2.2);
      const i = (y * size + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(alpha * 255);
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

function createStarBurstTexture(): THREE.DataTexture {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  const c = (size - 1) / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - c) / c;
      const dy = (y - c) / c;
      const dist = Math.min(1, Math.sqrt(dx * dx + dy * dy));
      const radial = Math.max(0, 1 - dist);
      const horizontalBar = Math.exp(-dy * dy * 80) * radial;
      const verticalBar = Math.exp(-dx * dx * 80) * radial;
      const center = Math.exp(-(dist * dist) * 9);
      const alpha = Math.min(1, horizontalBar + verticalBar + center * 0.7);
      const i = (y * size + x) * 4;
      data[i] = 255;
      data[i + 1] = 255;
      data[i + 2] = 255;
      data[i + 3] = Math.round(alpha * 255);
    }
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}
